import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Coins, Crown } from "lucide-react";
import { toast } from "sonner";

const emptyPkg = {
  name: "",
  credits: 100,
  price_usd: 10,
  price_mxn: 200,
  active: true,
  sort_order: 0,
};
const emptyPlan = {
  name: "",
  duration_days: 30,
  price_usd: 19.99,
  price_mxn: 399,
  unlimited: true,
  credits_included: 0,
  active: true,
  sort_order: 0,
};

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [plans, setPlans] = useState([]);
  const [editPkg, setEditPkg] = useState(null);
  const [editPlan, setEditPlan] = useState(null);

  const load = async () => {
    const [p1, p2] = await Promise.all([api.get("/packages"), api.get("/plans")]);
    setPackages(p1.data);
    setPlans(p2.data);
  };
  useEffect(() => {
    load();
  }, []);

  const savePackage = async () => {
    try {
      const { id, created_at, ...payload } = editPkg;
      if (id) {
        await api.put(`/packages/${id}`, payload);
      } else {
        await api.post("/packages", payload);
      }
      toast.success("Guardado");
      setEditPkg(null);
      load();
    } catch (e) {
      toast.error("Error al guardar");
    }
  };

  const savePlan = async () => {
    try {
      const { id, created_at, ...payload } = editPlan;
      if (id) await api.put(`/plans/${id}`, payload);
      else await api.post("/plans", payload);
      toast.success("Guardado");
      setEditPlan(null);
      load();
    } catch (e) {
      toast.error("Error al guardar");
    }
  };

  const delPackage = async (id) => {
    if (!confirm("¿Eliminar paquete?")) return;
    await api.delete(`/packages/${id}`);
    toast.success("Eliminado");
    load();
  };

  const delPlan = async (id) => {
    if (!confirm("¿Eliminar plan?")) return;
    await api.delete(`/plans/${id}`);
    toast.success("Eliminado");
    load();
  };

  return (
    <div className="space-y-6 max-w-[1200px]" data-testid="packages-page">
      <div>
        <div className="overline mb-2">Catálogo</div>
        <h1 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight">Paquetes & Planes</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Configura los productos que se mostrarán a los usuarios cuando usen /comprar en Telegram.
        </p>
      </div>

      <Tabs defaultValue="packages" className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm p-1">
          <TabsTrigger value="packages" data-testid="tab-packages" className="rounded-sm data-[state=active]:bg-zinc-800">
            <Coins size={14} className="mr-2" /> Créditos ({packages.length})
          </TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans" className="rounded-sm data-[state=active]:bg-zinc-800">
            <Crown size={14} className="mr-2" /> Membresías ({plans.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="packages" className="mt-6 space-y-3">
          <div className="flex justify-end">
            <Button
              onClick={() => setEditPkg({ ...emptyPkg })}
              data-testid="create-package-button"
              className="bg-[#2AABEE] hover:bg-[#1C93D4] rounded-sm"
            >
              <Plus size={14} className="mr-1" /> Nuevo paquete
            </Button>
          </div>
          {packages.map((p) => (
            <div
              key={p.id}
              className="bg-zinc-900/70 border border-zinc-800 rounded-sm p-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-4 items-center"
              data-testid={`package-${p.id}`}
            >
              <div>
                <div className="font-display font-bold text-lg">{p.name}</div>
                <div className="text-xs text-zinc-500">
                  <span className="font-mono">{p.credits}</span> créditos ·
                  <span className="font-mono text-emerald-400"> ${p.price_usd} USD</span> ·
                  <span className="font-mono text-emerald-400"> ${p.price_mxn} MXN</span>
                </div>
              </div>
              <span
                className={`text-[10px] font-mono font-bold px-2 py-1 rounded-sm border ${p.active ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-zinc-800 text-zinc-500 border-zinc-700"}`}
              >
                {p.active ? "ACTIVO" : "INACTIVO"}
              </span>
              <Button variant="ghost" size="sm" onClick={() => setEditPkg(p)} data-testid={`edit-package-${p.id}`}>
                Editar
              </Button>
              <Button variant="ghost" size="sm" onClick={() => delPackage(p.id)} data-testid={`delete-package-${p.id}`}>
                <Trash2 size={14} className="text-red-400" />
              </Button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="plans" className="mt-6 space-y-3">
          <div className="flex justify-end">
            <Button
              onClick={() => setEditPlan({ ...emptyPlan })}
              data-testid="create-plan-button"
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm"
            >
              <Plus size={14} className="mr-1" /> Nuevo plan
            </Button>
          </div>
          {plans.map((p) => (
            <div
              key={p.id}
              className="bg-zinc-900/70 border border-zinc-800 rounded-sm p-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-4 items-center"
              data-testid={`plan-${p.id}`}
            >
              <div>
                <div className="font-display font-bold text-lg">{p.name}</div>
                <div className="text-xs text-zinc-500">
                  <span className="font-mono">{p.duration_days}d</span> ·
                  <span className="font-mono text-emerald-400"> ${p.price_usd} USD</span> ·
                  <span className="font-mono text-emerald-400"> ${p.price_mxn} MXN</span>
                  {p.unlimited && <span className="ml-2 text-amber-400">· ilimitado</span>}
                </div>
              </div>
              <span
                className={`text-[10px] font-mono font-bold px-2 py-1 rounded-sm border ${p.active ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-zinc-800 text-zinc-500 border-zinc-700"}`}
              >
                {p.active ? "ACTIVO" : "INACTIVO"}
              </span>
              <Button variant="ghost" size="sm" onClick={() => setEditPlan(p)} data-testid={`edit-plan-${p.id}`}>
                Editar
              </Button>
              <Button variant="ghost" size="sm" onClick={() => delPlan(p.id)} data-testid={`delete-plan-${p.id}`}>
                <Trash2 size={14} className="text-red-400" />
              </Button>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      {/* Package dialog */}
      <Dialog open={!!editPkg} onOpenChange={() => setEditPkg(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editPkg?.id ? "Editar paquete" : "Nuevo paquete"}
            </DialogTitle>
          </DialogHeader>
          {editPkg && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Nombre</Label>
                <Input value={editPkg.name} onChange={(e) => setEditPkg({ ...editPkg, name: e.target.value })} className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="pkg-name-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Créditos</Label>
                  <Input type="number" value={editPkg.credits} onChange={(e) => setEditPkg({ ...editPkg, credits: Number(e.target.value) })} className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="pkg-credits-input" />
                </div>
                <div>
                  <Label className="text-xs">Precio USD</Label>
                  <Input type="number" step="0.01" value={editPkg.price_usd} onChange={(e) => setEditPkg({ ...editPkg, price_usd: Number(e.target.value) })} className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="pkg-price-usd-input" />
                </div>
                <div>
                  <Label className="text-xs">Precio MXN</Label>
                  <Input type="number" step="0.01" value={editPkg.price_mxn} onChange={(e) => setEditPkg({ ...editPkg, price_mxn: Number(e.target.value) })} className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="pkg-price-mxn-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Orden</Label>
                  <Input type="number" value={editPkg.sort_order} onChange={(e) => setEditPkg({ ...editPkg, sort_order: Number(e.target.value) })} className="bg-zinc-950 border-zinc-800 rounded-sm" />
                </div>
                <div className="flex items-end gap-2 pb-2">
                  <Switch checked={editPkg.active} onCheckedChange={(v) => setEditPkg({ ...editPkg, active: v })} data-testid="pkg-active-switch" />
                  <Label className="text-xs">Activo</Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditPkg(null)}>Cancelar</Button>
            <Button onClick={savePackage} data-testid="save-package-button" className="bg-[#2AABEE] hover:bg-[#1C93D4] rounded-sm">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan dialog */}
      <Dialog open={!!editPlan} onOpenChange={() => setEditPlan(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editPlan?.id ? "Editar plan" : "Nuevo plan"}
            </DialogTitle>
          </DialogHeader>
          {editPlan && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Nombre</Label>
                <Input value={editPlan.name} onChange={(e) => setEditPlan({ ...editPlan, name: e.target.value })} className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="plan-name-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Duración (días)</Label>
                  <Input type="number" value={editPlan.duration_days} onChange={(e) => setEditPlan({ ...editPlan, duration_days: Number(e.target.value) })} className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="plan-days-input" />
                </div>
                <div>
                  <Label className="text-xs">Precio USD</Label>
                  <Input type="number" step="0.01" value={editPlan.price_usd} onChange={(e) => setEditPlan({ ...editPlan, price_usd: Number(e.target.value) })} className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="plan-price-usd-input" />
                </div>
                <div>
                  <Label className="text-xs">Precio MXN</Label>
                  <Input type="number" step="0.01" value={editPlan.price_mxn} onChange={(e) => setEditPlan({ ...editPlan, price_mxn: Number(e.target.value) })} className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="plan-price-mxn-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-end gap-2 pb-2">
                  <Switch checked={editPlan.unlimited} onCheckedChange={(v) => setEditPlan({ ...editPlan, unlimited: v })} data-testid="plan-unlimited-switch" />
                  <Label className="text-xs">Mensajes ilimitados</Label>
                </div>
                {!editPlan.unlimited && (
                  <div>
                    <Label className="text-xs">Créditos incluidos</Label>
                    <Input type="number" value={editPlan.credits_included} onChange={(e) => setEditPlan({ ...editPlan, credits_included: Number(e.target.value) })} className="bg-zinc-950 border-zinc-800 rounded-sm" />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editPlan.active} onCheckedChange={(v) => setEditPlan({ ...editPlan, active: v })} data-testid="plan-active-switch" />
                <Label className="text-xs">Activo</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditPlan(null)}>Cancelar</Button>
            <Button onClick={savePlan} data-testid="save-plan-button" className="bg-amber-500 hover:bg-amber-600 text-black rounded-sm">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
